import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Box,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@mui/material';
import {
  Search,
  ViewModule,
  ViewList,
  Add,
  CloudUpload
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getUserDocuments, 
  deleteDocument,
  downloadDocument,
  toggleDocumentFavorite,
  renameDocument,
  uploadDocument
} from '../../services/documentStorageService';
import { getDocumentProgress } from '../../services/progressService';
import { trackEvent } from '../../services/analyticsService';
import DocumentCard from './DocumentCard';

const DocumentLibrary = ({ onOpenDocument }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [documentProgress, setDocumentProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [viewMode, setViewMode] = useState('grid');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [upgradePrompt, setUpgradePrompt] = useState(null);

  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const { documents: userDocs } = await getUserDocuments(user);
      setDocuments(userDocs);

      // Load progress for each document
      const progressMap = {};
      for (const doc of userDocs) {
        try {
          const { data: progress } = await getDocumentProgress(doc.document_hash, user);
          if (progress) {
            progressMap[doc.id] = progress;
          }
        } catch (error) {
          console.warn(`Failed to load progress for document ${doc.id}:`, error);
        }
      }
      setDocumentProgress(progressMap);
    } catch (error) {
      console.error('Error loading documents:', error);
      showSnackbar('Failed to load documents', 'error');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load documents and their progress
  useEffect(() => {
    if (user) {
      loadDocuments();
    } else {
      setLoading(false);
    }
  }, [user, loadDocuments]);

  // Filter and sort documents
  useEffect(() => {
    let filtered = [...documents];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(doc =>
        doc.original_filename.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply type filter
    if (filterType !== 'all') {
      if (filterType === 'pdf') {
        filtered = filtered.filter(doc => doc.file_type === 'application/pdf');
      } else if (filterType === 'text') {
        filtered = filtered.filter(doc => doc.file_type !== 'application/pdf');
      } else if (filterType === 'favorites') {
        filtered = filtered.filter(doc => doc.is_favorite);
      }
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.original_filename.localeCompare(b.original_filename);
        case 'size':
          return b.file_size - a.file_size;
        case 'recent':
        default:
          return new Date(b.last_opened) - new Date(a.last_opened);
      }
    });
    
    setFilteredDocuments(filtered);
  }, [documents, searchTerm, filterType, sortBy]);

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    let successCount = 0;
    let errorCount = 0;
    let capError = null;

    for (const file of files) {
      try {
        await uploadDocument(file, user);
        successCount++;
      } catch (error) {
        console.error('Upload error:', error);
        errorCount++;
        if (error?.code === 'LIBRARY_DOC_CAP_REACHED' || error?.code === 'LIBRARY_SIZE_CAP_REACHED') {
          capError = error;
        }
      }
    }

    setUploading(false);
    setUploadDialogOpen(false);

    if (successCount > 0) {
      showSnackbar(`Successfully uploaded ${successCount} document(s)`, 'success');
      loadDocuments(); // Refresh the list
    }

    if (errorCount > 0) {
      if (capError?.code === 'LIBRARY_DOC_CAP_REACHED') {
        setUpgradePrompt({
          title: 'Library limit reached',
          message: capError.message || 'Free plan library is full. Upgrade to save more documents.',
        });
      } else if (capError?.code === 'LIBRARY_SIZE_CAP_REACHED') {
        setUpgradePrompt({
          title: 'Storage full',
          message: capError.message || 'Free plan storage is full. Upgrade to save larger libraries.',
        });
      } else {
        showSnackbar(`Failed to upload ${errorCount} document(s)`, 'error');
      }
    }
  };

  const handleSeePlans = () => {
    navigate('/usage?plan=yearly');
  };

  const handleOpenDocument = async (document) => {
    try {
      trackEvent('reader_opened', {
        source: 'library',
        fileType: document?.file_type || 'unknown',
      });
      navigate(`/reader?docId=${document.id}`);
      
      // Refresh documents to update last_opened
      loadDocuments();
    } catch (error) {
      console.error('Error opening document:', error);
      showSnackbar('Failed to open document', 'error');
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await deleteDocument(documentId, user);
      showSnackbar('Document deleted successfully', 'success');
      loadDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      showSnackbar('Failed to delete document', 'error');
    }
  };

  const handleRenameDocument = async (documentId, newName) => {
    try {
      await renameDocument(documentId, newName, user);
      showSnackbar('Document renamed successfully', 'success');
      loadDocuments();
    } catch (error) {
      console.error('Error renaming document:', error);
      showSnackbar('Failed to rename document', 'error');
    }
  };

  const handleToggleFavorite = async (documentId) => {
    try {
      await toggleDocumentFavorite(documentId, user);
      loadDocuments();
    } catch (error) {
      console.error('Error toggling favorite:', error);
      showSnackbar('Failed to update favorite status', 'error');
    }
  };

  const handleDownloadDocument = async (document) => {
    try {
      const { url } = await downloadDocument(document, user);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = document.original_filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showSnackbar('Download started', 'success');
    } catch (error) {
      console.error('Error downloading document:', error);
      showSnackbar('Failed to download document', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getFilterChips = () => {
    const chips = [];
    if (searchTerm) {
      chips.push(
        <Chip
          key="search"
          label={`Search: ${searchTerm}`}
          onDelete={() => setSearchTerm('')}
          size="small"
        />
      );
    }
    if (filterType !== 'all') {
      chips.push(
        <Chip
          key="filter"
          label={`Filter: ${filterType}`}
          onDelete={() => setFilterType('all')}
          size="small"
        />
      );
    }
    return chips;
  };

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">
          Please sign in to access your document library and store files permanently.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading your documents...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Reading Library
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Pick up where you left off.
        </Typography>
      </Box>

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Search */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>

          {/* Filter */}
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter</InputLabel>
              <Select
                value={filterType}
                label="Filter"
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="all">All Files</MenuItem>
                <MenuItem value="pdf">PDFs</MenuItem>
                <MenuItem value="text">Text Files</MenuItem>
                <MenuItem value="favorites">Favorites</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Sort */}
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort by</InputLabel>
              <Select
                value={sortBy}
                label="Sort by"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="recent">Recent</MenuItem>
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="size">Size</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* View Mode */}
          <Grid item xs={6} md={2}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
              fullWidth
              sx={{
                width: '100%',
                pointerEvents: 'auto',
                touchAction: 'manipulation',
              }}
            >
              <ToggleButton value="grid" onClick={() => setViewMode('grid')} sx={{ flex: 1 }}>
                <ViewModule />
              </ToggleButton>
              <ToggleButton value="list" onClick={() => setViewMode('list')} sx={{ flex: 1 }}>
                <ViewList />
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          {/* Upload Button */}
          <Grid item xs={6} md={2}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setUploadDialogOpen(true)}
              fullWidth
            >
              Upload
            </Button>
          </Grid>
        </Grid>

        {/* Active Filters */}
        {getFilterChips().length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {getFilterChips()}
          </Box>
        )}
      </Paper>

      {/* Documents Grid/List */}
      {filteredDocuments.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CloudUpload sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {documents.length === 0 ? 'No documents yet' : 'No documents match your filters'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {documents.length === 0 
              ? 'Upload your first document to get started'
              : 'Try adjusting your search or filter criteria'
            }
          </Typography>
          {documents.length === 0 && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setUploadDialogOpen(true)}
            >
              Upload Document
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredDocuments.map((document) => (
            <Grid 
              item 
              xs={12} 
              sm={viewMode === 'grid' ? 6 : 12} 
              md={viewMode === 'grid' ? 4 : 12} 
              lg={viewMode === 'grid' ? 3 : 12}
              key={document.id}
            >
              <DocumentCard
                document={document}
                progress={documentProgress[document.id]}
                viewMode={viewMode}
                onOpen={handleOpenDocument}
                onDelete={handleDeleteDocument}
                onRename={handleRenameDocument}
                onToggleFavorite={handleToggleFavorite}
                onDownload={handleDownloadDocument}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Documents</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              border: '2px dashed',
              borderColor: 'primary.main',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
            onClick={() => document.getElementById('file-upload').click()}
          >
            <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Drop files here or click to browse
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Supports PDF and text files
            </Typography>
            <input
              id="file-upload"
              type="file"
              multiple
              accept=".pdf,.txt,.md"
              style={{ display: 'none' }}
              onChange={(e) => handleFileUpload(Array.from(e.target.files))}
            />
          </Box>
          {uploading && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ mt: 1 }}>
                Uploading documents...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(upgradePrompt)} onClose={() => setUpgradePrompt(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{upgradePrompt?.title || 'Upgrade'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {upgradePrompt?.message || 'Upgrade to unlock higher limits.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpgradePrompt(null)}>Not now</Button>
          <Button variant="contained" onClick={handleSeePlans}>See premium plans</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DocumentLibrary;
